import type { ReactNode } from "react";
import { Link } from "wouter";
import type { CtaLink } from "@shared/schema";

/**
 * Selectable "scroll to section" targets on the home page. The `value` matches
 * the `data-preview-section` attribute rendered in home.tsx, and the `label` is
 * shown in the admin section dropdown.
 */
export const HOME_ANCHOR_SECTIONS: { value: string; label: string }[] = [
  { value: "mortgage-advisor", label: "Hitelügyintézés" },
  { value: "services", label: "Szolgáltatások" },
  { value: "projects", label: "Projektek" },
  { value: "cta", label: "Kapcsolat (CTA sáv)" },
  { value: "partners", label: "Partnereink" },
];

/** Smooth-scroll to the `data-preview-section` element on the current page. */
export function scrollToSection(section: string) {
  if (typeof document === "undefined") return;
  const el = document.querySelector(`[data-preview-section="${section}"]`);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

/**
 * Resolve a CTA link config to a concrete navigation, falling back to
 * `fallbackHref` (an internal page) when no link is configured. Backwards
 * compatible: legacy settings rows without a link keep their historical target.
 */
export function CtaButton({
  link,
  fallbackHref,
  className,
  children,
}: {
  link?: CtaLink | null;
  fallbackHref: string;
  className?: string;
  children: ReactNode;
}) {
  const type = link?.linkType ?? "page";
  const value = link?.linkValue?.trim() || (type === "page" ? fallbackHref : "");

  // Anchor → smooth-scroll to a section on the page.
  if (type === "anchor" && value) {
    return (
      <button
        type="button"
        className={className}
        onClick={() => scrollToSection(value)}
      >
        {children}
      </button>
    );
  }

  // External URL → open in a new tab.
  if (type === "url" && value) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {children}
      </a>
    );
  }

  // Internal page (default) — use the configured value or the historical target.
  return (
    <Link href={value || fallbackHref} className={className}>
      {children}
    </Link>
  );
}
